import { StateManager } from '../../managers/StateManager';
import { elementColor } from '../../utils/elementColors';
import { getElementIconUrl } from './ItemIcon';
import { formatElement } from './GameTerms';

/**
 * Hover/long-press tooltip for an element / defensive type.
 *
 * Lists what hits the type for super-effective damage (Weak) and what the type
 * blunts (Resists). Reads the live elementChart off configData so any chart
 * edit propagates without code changes here. Used by combat HP bars and any
 * other surface that displays defensive-type chips.
 */
export function buildElementTooltip(element: string): string {
  const config = StateManager.get('configData');
  const chart = config?.elementChart;
  const color = elementColor(element);
  const name = formatElement(element);

  const header =
    `<div class="tooltip-header">` +
      `<span class="tooltip-name-group">` +
        `<img class="tooltip-icon" src="${getElementIconUrl(element)}" alt="">` +
        `<span class="tooltip-name" style="color:${color}">${name}</span>` +
      `</span>` +
    `</div>`;

  if (!chart) {
    return `<div class="tooltip-inner">${header}</div>`;
  }

  const weakTo: string[] = [];
  const resists: string[] = [];
  for (const atk of Object.keys(chart)) {
    const val = chart[atk]?.[element] ?? 0;
    if (val < 0) weakTo.push(atk);
    else if (val > 0 && atk !== element) resists.push(atk);
  }
  const selfRes = (chart[element]?.[element] ?? 0) > 0;

  const colorize = (els: string[]) =>
    els.map(e => `<span style="color:${elementColor(e)}">${formatElement(e)}</span>`).join(', ');

  let body = '';
  if (weakTo.length > 0) {
    body += `<div class="tooltip-line"><span class="label">Weak:</span> ${colorize(weakTo)}</div>`;
  }
  if (resists.length > 0 || selfRes) {
    const list = selfRes ? [...resists, element] : resists;
    body += `<div class="tooltip-line"><span class="label">Resists:</span> ${colorize(list)}</div>`;
  }
  if (!body) {
    body = `<div class="tooltip-line"><span class="label">Neutral against all types.</span></div>`;
  }

  return `<div class="tooltip-inner">${header}${body}</div>`;
}
